/**
 * Add/Edit Service dialog (DNKADM-0055 U1) — a MUI `Dialog` that creates/updates a
 * `v2_service` entity via `createService` / `updateService` (handoff §5.5).
 *
 * Core fields: **name** (required — unique on `(tenant, name)`), **description**
 * (optional), **imageUrl** (optional — a plain URL, §Non-Goals), the **credit bucket**
 * (1:1; "None — free service" when unset, via the 0052 fail-closed bucket pick-list),
 * the **staff** + **resource** assignment-mode pickers (`NONE|MEMBER_SELECTS|
 * ROUND_ROBIN`, §4.7), and the **taxable** boolean.
 *
 * **Invariant enforced inline (§4.7):** at least one of staff/resource assignment ≠
 * NONE — the save is blocked with a field error when both are NONE. The category /
 * location / staff / resource ASSIGNMENTS themselves (the join rows) are managed on the
 * service detail (U2), not here; this dialog owns only the core record + the two modes.
 *
 * The presentation pair (DNKADM-0267): **variation display** (a two-option select —
 * durations vs named types, the closed DNKAPI-0421 vocabulary) and **featured**
 * (member-app highlight). Both are preserve-on-null on the wire — an unset control
 * is OMITTED from the body, never sent as a blank.
 *
 * The **booking lead time override** (DNKADM-0385, `@/domain/leadTime`): blank inherits
 * the tenant default and the helper line shows the inherited figure (one
 * `GET /v2/admin/tenant-settings` per open, fail-soft); a set value wins, even when
 * shorter. Written through the billing-day sentinel like `LocationDialog`: on edit the
 * key is always sent (`0` = clear/inherit, `n` = set); on create only when filled.
 *
 * A validation `400` / `409` renders INLINE; anything else raises an error toast
 * (`classifyFormError`). Mirrors `ResourceDialog` (DNKADM-0054).
 */
import { useCallback, useId, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import {
  createService,
  updateService,
  type PaymentMode,
  type ServiceInput,
  type V2Service,
  type VariationDisplay,
} from '@/api/services';
import { useCreditBucketPickList } from '@/api/creditBuckets';
import { getTenantSettings } from '@/api/tenantSettings';
import { PickListLoader } from '@/components/PickListLoader';
import {
  ASSIGNMENT_MODES,
  assignmentModeLabel,
  type AssignmentMode,
} from '@/domain/v2Enums';
import {
  LEAD_TIME_BEHAVIOUR,
  leadTimeMinutesLabel,
  parseLeadTimeEntry,
  splitLeadTime,
  type LeadTimeUnit,
} from '@/domain/leadTime';
import { LeadTimeInput } from '@/components/LeadTimeInput';
import { useResource } from '@/hooks/useResource';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';

export interface ServiceDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The service being edited, or `null` to add a new one. */
  service: V2Service | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

interface FieldErrors {
  name?: string;
  assignment?: string;
  maxGuests?: string;
  leadTimeMinutes?: string;
}

/**
 * The lead-time helper line (DNKADM-0385 U2/U3). Blank inherits the tenant default, so
 * the line says what will ACTUALLY apply — the inherited number, read from the tenant
 * settings — and an operator never opens two screens to answer "how much notice does
 * this service need". `tenant` is `undefined` while that read is pending or failed
 * (fail-soft: the override still saves; only the inherited figure is unknown).
 */
function leadTimeHelp(raw: string, unit: LeadTimeUnit, tenant: number | null | undefined): string {
  // Branch on the parsed value, not the raw text: a typed 0 means "inherit" (spec U1's
  // "blank or zero"), so it reads the inherit line, never "overrides".
  const parsed = parseLeadTimeEntry(raw, unit);
  if (parsed.ok && parsed.value !== null) {
    return `Overrides the tenant lead time, even when shorter. ${LEAD_TIME_BEHAVIOUR}`;
  }
  const inherited =
    tenant === undefined
      ? 'Empty inherits the tenant lead time.'
      : tenant === null
        ? 'Empty inherits the tenant lead time — currently none, so members can book right up to the start.'
        : `Empty inherits the tenant lead time — currently ${leadTimeMinutesLabel(tenant)}.`;
  return `${inherited} ${LEAD_TIME_BEHAVIOUR}`;
}

/** Parse a non-negative integer from text, or null when invalid/blank. */
function parseNonNegativeInt(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : null;
}

export function ServiceDialog(props: ServiceDialogProps) {
  // Remount the inner form per open / per edited service so its inputs reseed.
  if (!props.open) return null;
  return <ServiceDialogForm key={props.service?.id ?? 'new'} {...props} />;
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** Sentinel value for the "None — free service" bucket option. */
const NO_BUCKET = '';

/** The two known payment-capture modes the UI offers (DNKAPI-0271). */
const PAYMENT_MODE_OPTIONS: { value: PaymentMode; label: string }[] = [
  { value: 'charge', label: 'Charge at booking' },
  { value: 'card_on_file', label: 'Card on file, no charge' },
];

/** Always-appended caveat: the mode governs new bookings only. */
const PAYMENT_MODE_CAVEAT =
  'Changing this affects new bookings; existing reservations keep the capture they were created with.';

/** Helper text: the per-option guidance (when known) plus the future-bookings caveat. */
function paymentModeHelp(mode: PaymentMode): string {
  const guidance =
    mode === 'charge'
      ? 'The guest pays for this service when they book.'
      : mode === 'card_on_file'
        ? "The guest’s card is saved at booking with no charge (e.g. swing evaluation). A no-show fee can be charged later."
        : '';
  return guidance === '' ? PAYMENT_MODE_CAVEAT : `${guidance} ${PAYMENT_MODE_CAVEAT}`;
}

function ServiceDialogForm({
  service,
  onClose,
  onSaved,
  showToast,
}: Omit<ServiceDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = service !== null;
  const buckets = useCreditBucketPickList();

  const [name, setName] = useState(service?.name ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [imageUrl, setImageUrl] = useState(service?.imageUrl ?? '');
  const [creditBucketId, setCreditBucketId] = useState<string>(
    service?.creditBucketId ?? NO_BUCKET,
  );
  const [staffAssignment, setStaffAssignment] = useState<AssignmentMode>(
    service?.staffAssignment ?? 'NONE',
  );
  const [resourceAssignment, setResourceAssignment] = useState<AssignmentMode>(
    service?.resourceAssignment ?? 'NONE',
  );
  const [taxable, setTaxable] = useState(service?.taxable ?? false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(
    service?.requiresConfirmation ?? false,
  );
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(
    service?.paymentMode ?? 'charge',
  );
  const [allowGuests, setAllowGuests] = useState(service?.allowGuests ?? false);
  // Held as text (like the variation number fields) so an empty/invalid entry is
  // validated, not silently coerced. Default "0" (= unlimited when guests are allowed).
  const [maxGuests, setMaxGuests] = useState(String(service?.maxGuests ?? 0));
  // Presentation pair (DNKADM-0267), both preserve-on-null on the wire: '' / undefined
  // mean "no selection" and are OMITTED from the body (create → server defaults;
  // update → the stored value is preserved). Only an actual selection is ever sent.
  const [variationDisplay, setVariationDisplay] = useState<VariationDisplay | ''>(
    service?.variationDisplay ?? '',
  );
  const [featured, setFeatured] = useState<boolean | undefined>(service?.featured);
  // Booking lead time override (DNKADM-0385; units DNKADM-0389) — blank when the service
  // inherits; when set, the largest unit that divides the stored minutes evenly. No default
  // number is ever seeded.
  const [leadTimeAmount, setLeadTimeAmount] = useState(
    service?.leadTimeMinutes !== undefined ? String(splitLeadTime(service.leadTimeMinutes).amount) : '',
  );
  const [leadTimeUnit, setLeadTimeUnit] = useState<LeadTimeUnit>(
    service?.leadTimeMinutes !== undefined ? splitLeadTime(service.leadTimeMinutes).unit : 'minutes',
  );
  // The tenant default the blank field inherits — read once per open so the helper
  // line can show the number that will actually apply. Fail-soft: a failed read only
  // hides the figure (`data` stays null → `undefined` below); the override still saves.
  const tenantSettings = useResource(useCallback(() => getTenantSettings(), []), []);
  const tenantLeadTime =
    tenantSettings.status === 'ready' && tenantSettings.data !== null
      ? tenantSettings.data.leadTimeMinutes
      : undefined;

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function validate(): ServiceInput | null {
    const errors: FieldErrors = {};
    const tName = name.trim();
    if (tName === '') errors.name = 'Enter a name.';
    // §4.7 invariant: at least one of staff/resource assignment ≠ NONE.
    if (staffAssignment === 'NONE' && resourceAssignment === 'NONE') {
      errors.assignment =
        'Set staff or resources (or both) to “Member selects” or “Auto-assign”. A service must use at least one.';
    }
    // Guest cap: only validated when guests are allowed (mirror the API — integer ≥ 0,
    // where `0` = unlimited is VALID). When guests are off, maxGuests is sent as 0.
    let maxGuestsValue = 0;
    if (allowGuests) {
      const parsed = parseNonNegativeInt(maxGuests);
      if (parsed === null) {
        errors.maxGuests = 'Enter 0 or a positive whole number (0 = unlimited).';
      } else {
        maxGuestsValue = parsed;
      }
    }
    // Lead time override (DNKADM-0385): blank OK (inherit); else a whole number 1–MAX.
    // Shorter than the tenant value is a legitimate override, never an error.
    const leadTimeResult = parseLeadTimeEntry(leadTimeAmount, leadTimeUnit);
    if (!leadTimeResult.ok) errors.leadTimeMinutes = leadTimeResult.message;
    if (errors.name || errors.assignment || errors.maxGuests || errors.leadTimeMinutes) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    const input: ServiceInput = {
      name: tName,
      staffAssignment,
      resourceAssignment,
      taxable,
      requiresConfirmation,
      paymentMode,
      allowGuests,
      maxGuests: maxGuestsValue,
      creditBucketId: creditBucketId === NO_BUCKET ? null : creditBucketId,
    };
    const d = optional(description);
    if (d !== undefined) input.description = d;
    const img = optional(imageUrl);
    if (img !== undefined) input.imageUrl = img;
    // DNKADM-0267 preserve-on-null: only an actual selection goes out — an unset
    // select/toggle is omitted, never sent as a blank that would overwrite.
    if (variationDisplay !== '') input.variationDisplay = variationDisplay;
    if (featured !== undefined) input.featured = featured;
    // Lead time override (DNKADM-0385) — the LocationDialog billing-day rule:
    // edit: always send (0 = clear → inherit, n = set); create: only when filled
    // (no override = omit the key; the server inherits).
    const leadTimeValue = leadTimeResult.ok ? leadTimeResult.value : null;
    if (isEdit) input.leadTimeMinutes = leadTimeValue !== null ? leadTimeValue : 0;
    else if (leadTimeValue !== null) input.leadTimeMinutes = leadTimeValue;
    return input;
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && service) await updateService(service.id, input);
      else await createService(input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      showToast(isEdit ? 'Service saved' : 'Service added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyFormError(
        err,
        'The service couldn’t be saved. Please try again.',
      );
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const bucketLabelId = `${ids}-bucket-label`;
  const staffLabelId = `${ids}-staff-label`;
  const resourceLabelId = `${ids}-resource-label`;
  const paymentLabelId = `${ids}-payment-label`;
  const variationDisplayLabelId = `${ids}-variation-display-label`;

  // The two known modes, plus the loaded value when it's an unknown future enum (so the
  // control shows it as selected rather than blanking — do not coerce to `charge`).
  const paymentModeOptions = PAYMENT_MODE_OPTIONS.some((o) => o.value === paymentMode)
    ? PAYMENT_MODE_OPTIONS
    : [...PAYMENT_MODE_OPTIONS, { value: paymentMode, label: paymentMode }];

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit service' : 'Add service'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />
          <TextField
            label="Image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            helperText="Optional — a link to an image for this service."
            slotProps={{ htmlInput: { 'aria-label': 'Image URL', inputMode: 'url' } }}
          />

          {/* Credit bucket (1:1; null = free) via the 0052 fail-closed pick-list. */}
          <PickListLoader list={buckets} label="credit buckets" loadingVariant="inline">
            {(options) => (
              <FormControl size="small" fullWidth disabled={saving}>
                <InputLabel id={bucketLabelId}>Credit bucket</InputLabel>
                <Select
                  labelId={bucketLabelId}
                  label="Credit bucket"
                  value={creditBucketId}
                  onChange={(e) => setCreditBucketId(e.target.value)}
                  slotProps={{ input: { 'aria-label': 'Credit bucket' } }}
                >
                  <MenuItem value={NO_BUCKET}>None — free service</MenuItem>
                  {options.map((opt) => (
                    <MenuItem key={opt.id} value={opt.id}>
                      {opt.name === '' ? opt.id : opt.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </PickListLoader>

          {/* Assignment modes (§4.7) — at least one ≠ NONE. */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" fullWidth disabled={saving} error={Boolean(fieldErrors.assignment)}>
              <InputLabel id={staffLabelId}>Staff selection</InputLabel>
              <Select
                labelId={staffLabelId}
                label="Staff selection"
                value={staffAssignment}
                onChange={(e) => setStaffAssignment(e.target.value as AssignmentMode)}
                slotProps={{ input: { 'aria-label': 'Staff selection' } }}
              >
                {ASSIGNMENT_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {assignmentModeLabel(mode)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth disabled={saving} error={Boolean(fieldErrors.assignment)}>
              <InputLabel id={resourceLabelId}>Resource selection</InputLabel>
              <Select
                labelId={resourceLabelId}
                label="Resource selection"
                value={resourceAssignment}
                onChange={(e) => setResourceAssignment(e.target.value as AssignmentMode)}
                slotProps={{ input: { 'aria-label': 'Resource selection' } }}
              >
                {ASSIGNMENT_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {assignmentModeLabel(mode)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {fieldErrors.assignment && (
            <Alert severity="error" role="alert" sx={{ mt: -1.5 }}>
              {fieldErrors.assignment}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={taxable}
                onChange={(e) => setTaxable(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { 'aria-label': 'Taxable' } }}
              />
            }
            label="Taxable (tax computed at booking from the location’s active rates)"
          />

          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Switch
                  checked={requiresConfirmation}
                  onChange={(e) => setRequiresConfirmation(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Requires confirmation' } }}
                />
              }
              label="Requires confirmation"
            />
            <FormHelperText>
              Bookings need operator approval before they’re confirmed.
            </FormHelperText>
          </Stack>

          {/* Booking lead time override (DNKADM-0385) — blank inherits the tenant default and
              the helper line shows the inherited figure; a set value wins, even when shorter. */}
          <LeadTimeInput
            label="Booking lead time (override)"
            amount={leadTimeAmount}
            unit={leadTimeUnit}
            onAmountChange={setLeadTimeAmount}
            onUnitChange={setLeadTimeUnit}
            disabled={saving}
            size="small"
            error={Boolean(fieldErrors.leadTimeMinutes)}
            helperText={
              fieldErrors.leadTimeMinutes ?? leadTimeHelp(leadTimeAmount, leadTimeUnit, tenantLeadTime)
            }
            amountAriaLabel="Booking lead time (override)"
            amountWidth={140}
          />

          {/* Payment capture (DNKAPI-0271) — a Select (extensible enum), not a boolean. The
              UI sends only the two known values; a loaded unknown value stays selectable. */}
          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id={paymentLabelId}>Payment at booking</InputLabel>
            <Select
              labelId={paymentLabelId}
              label="Payment at booking"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value as PaymentMode)}
              slotProps={{ input: { 'aria-label': 'Payment at booking' } }}
            >
              {paymentModeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{paymentModeHelp(paymentMode)}</FormHelperText>
          </FormControl>

          {/* Guest policy (DNKAPI-0145): a master toggle revealing the per-reservation cap. */}
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={allowGuests}
                  onChange={(e) => setAllowGuests(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Allow guests' } }}
                />
              }
              label="Allow guests (members may bring guests on a reservation)"
            />
            {allowGuests && (
              <TextField
                label="Max guests per reservation"
                type="number"
                value={maxGuests}
                onChange={(e) => setMaxGuests(e.target.value)}
                disabled={saving}
                size="small"
                error={Boolean(fieldErrors.maxGuests)}
                helperText={fieldErrors.maxGuests ?? '0 = unlimited.'}
                sx={{ maxWidth: 280 }}
                slotProps={{
                  htmlInput: {
                    'aria-label': 'Max guests per reservation',
                    min: 0,
                    step: 1,
                    inputMode: 'numeric',
                  },
                }}
              />
            )}
          </Stack>

          {/* Presentation pair (DNKADM-0267) — display-only; the select can only ever
              produce the closed duration/type vocabulary; unset is omitted (preserve). */}
          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id={variationDisplayLabelId}>Show variations as</InputLabel>
            <Select
              labelId={variationDisplayLabelId}
              label="Show variations as"
              value={variationDisplay}
              onChange={(e) => setVariationDisplay(e.target.value as VariationDisplay)}
              slotProps={{ input: { 'aria-label': 'Show variations as' } }}
            >
              <MenuItem value="duration">Show variations as durations</MenuItem>
              <MenuItem value="type">Show variations as named types</MenuItem>
            </Select>
            <FormHelperText>
              How this service’s variations appear when booking. Left unset, durations
              are used.
            </FormHelperText>
          </FormControl>

          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Switch
                  checked={featured ?? false}
                  onChange={(e) => setFeatured(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Feature this service in the member app' } }}
                />
              }
              label="Feature this service in the member app"
            />
            <FormHelperText>
              Highlights this service to members. Display-only.
            </FormHelperText>
          </Stack>

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save service' : 'Add service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
